import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster, toast } from "sonner";
import {
  Home as HomeIcon, MapPin, Car, Lightbulb, Building2, ArrowLeftRight,
  Search, Clock, Loader2, Navigation, Globe, TrendingUp, Menu, X,
  Share2, Copy, Check, ArrowLeft, ChevronRight, ChevronDown,
  Shield, Zap, Target, ArrowRight, Sparkles, FileText,
  Bookmark, BookmarkCheck, TrendingDown, Eye, DollarSign, Trash2,
  Users, GraduationCap, Briefcase, PiggyBank, ChevronLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_areacosts/artifacts/eqoyg1oa_AffordlyAI%20icon.png";

const CURRENCY_MAP = {
  GB: "£", US: "$", NG: "₦", IN: "₹", GH: "₵", ZA: "R", KE: "KSh",
  CA: "C$", AU: "A$", DE: "€", FR: "€", IE: "€", AE: "AED", SG: "S$"
};

// ==================== LOCATION DATA ====================
const LOCATIONS = {
  // UK Cities
  london: { name: "London", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "9 million", avgRent: "£1,800/mo", avgHouse: "£530,000", img: "https://images.unsplash.com/photo-1652569612521-88a62e7e85be?w=600&h=400&fit=crop", desc: "London is the most expensive city in the UK. Rent alone can eat up half your salary if you're not careful. That said, wages tend to be higher here too. If you're considering London, run the numbers first — the gap between what you earn and what you spend is what actually matters." },
  manchester: { name: "Manchester", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "2.8 million", avgRent: "£950/mo", avgHouse: "£250,000", img: "https://images.unsplash.com/photo-1638637869635-f1a90c8f7edd?w=600&h=400&fit=crop", desc: "Manchester offers a solid balance between career opportunities and affordable living. You'll pay roughly half what London charges for rent, while still having access to a strong job market. It's one of the fastest-growing cities in the UK for a reason." },
  birmingham: { name: "Birmingham", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "2.6 million", avgRent: "£850/mo", avgHouse: "£230,000", img: "https://images.unsplash.com/photo-1633779452521-062054e164b9?w=600&h=400&fit=crop", desc: "Birmingham sits right in the middle of the country, and its costs sit in the middle too. Decent transport links to London without London prices. A practical choice for families and young professionals alike." },
  leeds: { name: "Leeds", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "800,000", avgRent: "£800/mo", avgHouse: "£220,000", img: "https://images.unsplash.com/photo-1713300701181-8fbb131ed756?w=600&h=400&fit=crop", desc: "Leeds has quietly become one of the best-value cities in the UK. A strong financial sector means good jobs, while housing costs remain well below the national average. Worth serious consideration if you want to build savings." },
  liverpool: { name: "Liverpool", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "900,000", avgRent: "£700/mo", avgHouse: "£185,000", desc: "Liverpool is genuinely affordable. Housing costs are among the lowest of any major UK city, and the regeneration over the past decade has brought new jobs and infrastructure. A lot of city for your money." },
  bristol: { name: "Bristol", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "470,000", avgRent: "£1,100/mo", avgHouse: "£340,000", desc: "Bristol is the priciest city in the South West, driven by a booming tech and creative sector. If you're moving here from London, you'll still save money. If you're moving from elsewhere in the region, be prepared for a step up." },
  edinburgh: { name: "Edinburgh", country: "United Kingdom", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", currency: "£", pop: "530,000", avgRent: "£1,000/mo", avgHouse: "£300,000", img: "https://images.unsplash.com/photo-1625947698855-dbf16524c685?w=600&h=400&fit=crop", desc: "Edinburgh is Scotland's most expensive city, but it's still significantly cheaper than London. The festival season pushes short-term rents up, so plan your move accordingly. Beautiful city with solid career prospects." },
  glasgow: { name: "Glasgow", country: "United Kingdom", flag: "🏴󠁧󠁢󠁳󠁣󠁴󠁿", currency: "£", pop: "1.7 million", avgRent: "£750/mo", avgHouse: "£180,000", desc: "Glasgow delivers a lot of city at a fraction of Edinburgh's cost. The gap between the two is wider than most people think. If you don't need to be in Edinburgh specifically, Glasgow deserves a hard look." },
  cardiff: { name: "Cardiff", country: "United Kingdom", flag: "🏴󠁧󠁢󠁷󠁬󠁳󠁿", currency: "£", pop: "370,000", avgRent: "£800/mo", avgHouse: "£225,000", desc: "Cardiff is one of the UK's most underrated cities for value. The Welsh capital offers affordable housing, good schools, and a compact city centre. Ideal if you want quality of life without a massive price tag." },
  nottingham: { name: "Nottingham", country: "United Kingdom", flag: "🇬🇧", currency: "£", pop: "330,000", avgRent: "£700/mo", avgHouse: "£200,000", desc: "Nottingham is a proper student city that also works well for young professionals. Rents are low, nightlife is good, and you're well connected to the rest of the Midlands. Don't sleep on it." },
  // Nigerian Cities
  lagos: { name: "Lagos", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "16 million", avgRent: "₦1.5M/yr", avgHouse: "₦45M", img: "https://images.unsplash.com/photo-1744907895363-d351aa6019ef?w=600&h=400&fit=crop", desc: "Lagos is Nigeria's economic engine and its most expensive city. Rent on the Island can swallow your entire salary if you're not strategic. Mainland offers better value, but traffic becomes a factor. Know your budget before committing to a neighbourhood." },
  abuja: { name: "Abuja", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "3.6 million", avgRent: "₦1.2M/yr", avgHouse: "₦35M", img: "https://images.unsplash.com/photo-1567985207911-725f4e7c8926?w=600&h=400&fit=crop", desc: "Abuja is planned, clean, and increasingly expensive. The political capital attracts federal workers and diplomats, pushing prices up in central areas. Satellite towns like Kubwa and Lugbe offer much better value if you don't mind the commute." },
  "port-harcourt": { name: "Port Harcourt", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "3.2 million", avgRent: "₦900K/yr", avgHouse: "₦25M", desc: "Port Harcourt's costs are driven by the oil industry. Expat areas like GRA are expensive, but move slightly further out and prices drop fast. The city has improved its infrastructure recently — worth checking current rates before making assumptions." },
  ibadan: { name: "Ibadan", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "3.6 million", avgRent: "₦350K/yr", avgHouse: "₦12M", img: "https://images.unsplash.com/photo-1646459273756-1337637ea7ce?w=600&h=400&fit=crop", desc: "Ibadan is one of the most affordable large cities in Nigeria. If you can work remotely, your money stretches incredibly far here. The ancient city is modernising quickly while keeping costs low. A genuine option for anyone watching their budget." },
  kano: { name: "Kano", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "4 million", avgRent: "₦400K/yr", avgHouse: "₦15M", desc: "Kano is the commercial hub of Northern Nigeria. Living costs are a fraction of what you'd pay in Lagos or Abuja. The city has a thriving business community and affordable housing stock. A practical choice for entrepreneurs." },
  kaduna: { name: "Kaduna", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "1.6 million", avgRent: "₦450K/yr", avgHouse: "₦14M", desc: "Kaduna sits between Abuja and Kano, both in geography and in pricing. It offers reasonable costs with improving infrastructure. New developments are bringing modern housing options without Lagos-level prices." },
  enugu: { name: "Enugu", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "900,000", avgRent: "₦400K/yr", avgHouse: "₦13M", desc: "Enugu is known as the Coal City, but these days it's better known for being clean, calm, and affordable. A strong choice for families who want space, greenery, and costs that don't keep them up at night." },
  "benin-city": { name: "Benin City", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "1.5 million", avgRent: "₦380K/yr", avgHouse: "₦12M", desc: "Benin City keeps things affordable. It's the capital of Edo State with a rich cultural history and costs that are well below the national average for cities its size. A solid option if you're looking for value in the South." },
  calabar: { name: "Calabar", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "600,000", avgRent: "₦350K/yr", avgHouse: "₦10M", desc: "Calabar is one of Nigeria's most affordable and most liveable cities. Clean streets, low crime, and housing costs that let you actually save money. The tourist economy keeps things moving without pushing prices too high." },
  lekki: { name: "Lekki", country: "Nigeria", flag: "🇳🇬", currency: "₦", pop: "Part of Lagos", avgRent: "₦2.5M/yr", avgHouse: "₦60M", desc: "Lekki is Lagos's premium suburb. You're paying for gated estates, proximity to the coast, and newer infrastructure. It's expensive by Nigerian standards, but offers a lifestyle that mainland Lagos can't match. Be realistic about what you can afford here." }
};

// ==================== UTILITY FUNCTIONS ====================
const haptic = (style = 'light') => {
  if (navigator.vibrate) {
    const patterns = { light: [10], medium: [20], success: [10, 50, 10], error: [50, 30, 50] };
    navigator.vibrate(patterns[style] || patterns.light);
  }
};

const RECENT_KEY = "affordly_recent_searches";
const SAVED_KEY = "affordly_saved_areas";

const saveRecentSearch = (location, data) => {
  const searches = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  const entry = { location, data, timestamp: new Date().toISOString() };
  const filtered = searches.filter(s => s.location.toLowerCase() !== location.toLowerCase());
  localStorage.setItem(RECENT_KEY, JSON.stringify([entry, ...filtered].slice(0, 10)));
};
const getRecentSearches = () => JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");

const saveArea = (location, data) => {
  const saved = JSON.parse(localStorage.getItem(SAVED_KEY) || "[]");
  if (saved.some(s => s.location.toLowerCase() === location.toLowerCase())) return false;
  const entry = { location, data, timestamp: new Date().toISOString() };
  localStorage.setItem(SAVED_KEY, JSON.stringify([entry, ...saved].slice(0, 20)));
  return true;
};
const getSavedAreas = () => JSON.parse(localStorage.getItem(SAVED_KEY) || "[]");
const removeArea = (location) => {
  const saved = JSON.parse(localStorage.getItem(SAVED_KEY) || "[]");
  localStorage.setItem(SAVED_KEY, JSON.stringify(saved.filter(s => s.location.toLowerCase() !== location.toLowerCase())));
};
const isAreaSaved = (location) => {
  const saved = JSON.parse(localStorage.getItem(SAVED_KEY) || "[]");
  return saved.some(s => s.location.toLowerCase() === location.toLowerCase());
};

const getScoreClass = (score) => {
  if (score >= 75) return "excellent";
  if (score >= 55) return "good";
  if (score >= 35) return "average";
  return "poor";
};
const getScoreLabel = (score) => {
  if (score >= 75) return "Very Affordable";
  if (score >= 55) return "Affordable";
  if (score >= 35) return "Average";
  return "Expensive";
};

// ==================== SHARED COMPONENTS ====================
const LoadingOverlay = ({ isVisible }) => {
  if (!isVisible) return null;
  return (
    <div className="loading-overlay" data-testid="loading-overlay">
      <div className="loading-content">
        <div className="loading-spinner"><div className="spinner-ring"></div><div className="spinner-ring"></div><div className="spinner-ring"></div></div>
        <p className="loading-text">Analysing costs...</p>
        <p className="privacy-text">We never store your location data</p>
      </div>
    </div>
  );
};

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path) => location.pathname === path;
  const go = (path) => { haptic('light'); setMobileMenuOpen(false); navigate(path); };

  return (
    <header className="app-header" data-testid="app-header">
      <div className="header-content">
        <Link to="/" className="logo-wrapper" data-testid="logo-link" onClick={() => haptic('light')}>
          <img src={LOGO_URL} alt="AffordlyAI" className="logo-img" />
          <span className="app-title">AffordlyAI</span>
        </Link>
        <nav className="header-nav desktop-nav" data-testid="desktop-nav">
          <button className={`nav-link ${isActive('/search') ? 'active' : ''}`} onClick={() => go('/search')} data-testid="nav-search"><Search className="h-4 w-4" /> Search</button>
          <button className={`nav-link ${isActive('/compare') ? 'active' : ''}`} onClick={() => go('/compare')} data-testid="nav-compare"><ArrowLeftRight className="h-4 w-4" /> Compare</button>
          <button className={`nav-link ${isActive('/saved') ? 'active' : ''}`} onClick={() => go('/saved')} data-testid="nav-saved"><Bookmark className="h-4 w-4" /> Saved</button>
          <button className={`nav-link ${isActive('/recent') ? 'active' : ''}`} onClick={() => go('/recent')} data-testid="nav-recent"><Clock className="h-4 w-4" /> Recent</button>
        </nav>
        <button className="mobile-menu-btn" onClick={() => { haptic('light'); setMobileMenuOpen(!mobileMenuOpen); }} data-testid="mobile-menu-btn">
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      {mobileMenuOpen && (
        <div className="mobile-menu animate-slideDown" data-testid="mobile-menu">
          <button className={`mobile-nav-link ${isActive('/search') ? 'active' : ''}`} onClick={() => go('/search')} data-testid="mobile-nav-search"><Search className="h-4 w-4" /> Search</button>
          <button className={`mobile-nav-link ${isActive('/compare') ? 'active' : ''}`} onClick={() => go('/compare')} data-testid="mobile-nav-compare"><ArrowLeftRight className="h-4 w-4" /> Compare</button>
          <button className={`mobile-nav-link ${isActive('/saved') ? 'active' : ''}`} onClick={() => go('/saved')} data-testid="mobile-nav-saved"><Bookmark className="h-4 w-4" /> Saved</button>
          <button className={`mobile-nav-link ${isActive('/recent') ? 'active' : ''}`} onClick={() => go('/recent')} data-testid="mobile-nav-recent"><Clock className="h-4 w-4" /> Recent</button>
          <div className="mobile-menu-divider"></div>
          <button className={`mobile-nav-link ${isActive('/about') ? 'active' : ''}`} onClick={() => go('/about')} data-testid="mobile-nav-about"><FileText className="h-4 w-4" /> About</button>
          <button className={`mobile-nav-link ${isActive('/privacy') ? 'active' : ''}`} onClick={() => go('/privacy')} data-testid="mobile-nav-privacy"><Shield className="h-4 w-4" /> Privacy</button>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="app-footer" data-testid="app-footer">
    <div className="footer-container">
      <div className="footer-grid">
        <div className="footer-brand-section">
          <Link to="/" className="footer-logo"><img src={LOGO_URL} alt="AffordlyAI" className="footer-logo-img" /><span>AffordlyAI</span></Link>
          <p className="footer-tagline">Your cost-of-living reality check. Know what you'll actually pay before you move.</p>
          <p className="footer-disclaimer">AI-generated estimates. Not financial advice.</p>
        </div>
        <div className="footer-links-section">
          <div className="footer-column">
            <h4>Product</h4>
            <Link to="/search">Search Areas</Link>
            <Link to="/compare">Compare Areas</Link>
            <Link to="/saved">Saved Areas</Link>
            <Link to="/recent">Recent Searches</Link>
          </div>
          <div className="footer-column">
            <h4>Company</h4>
            <Link to="/about">About</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </div>
      <div className="footer-bottom"><p>&copy; {new Date().getFullYear()} AffordlyAI. All rights reserved.</p></div>
    </div>
  </footer>
);

const AffordabilityScore = ({ score }) => {
  const cls = getScoreClass(score);
  const label = getScoreLabel(score);
  return (
    <div className="score-section animate-fadeInUp" data-testid="affordability-score">
      <div className="score-card">
        <div className={`score-circle ${cls}`} data-testid="score-value">{score}</div>
        <div className="score-info">
          <h3>Affordability Score</h3>
          <p>How affordable this area is compared to the national average. Higher is better.</p>
          <span className={`score-label ${cls}`}>{label}</span>
        </div>
      </div>
    </div>
  );
};

const CostCard = ({ icon: Icon, title, data, colorClass, frequencyLabel }) => (
  <Card className="cost-card animate-fadeInUp" data-testid={`cost-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className={`icon-wrapper ${colorClass}`}><Icon className="h-5 w-5" /></div>
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg font-semibold" style={{color: 'var(--text-primary)'}}>{title}</CardTitle>
          {frequencyLabel && <Badge variant="outline" className="frequency-badge">{frequencyLabel}</Badge>}
        </div>
      </div>
    </CardHeader>
    <CardContent>
      {data && typeof data === 'object' ? (
        <div className="space-y-3">
          {Object.entries(data).filter(([key]) => key !== 'description' && key !== 'frequency').map(([key, value]) => (
            <div key={key} className="flex justify-between items-center">
              <span className="text-sm capitalize" style={{color: 'var(--text-muted)'}}>{key.replace(/_/g, ' ')}</span>
              <span className="font-semibold" style={{color: 'var(--text-primary)'}}>{value}</span>
            </div>
          ))}
          {data.description && <p className="text-sm mt-3 pt-3" style={{color: 'var(--text-secondary)', borderTop: '1px solid var(--border)'}}>{data.description}</p>}
        </div>
      ) : <p style={{color: 'var(--text-muted)'}}>No data available</p>}
    </CardContent>
  </Card>
);

const ComparisonCard = ({ icon: Icon, title, data1, data2, location1, location2, colorClass, freq1, freq2 }) => (
  <Card className="comparison-card animate-fadeInUp">
    <CardHeader className="pb-3">
      <div className="flex items-center gap-3">
        <div className={`icon-wrapper ${colorClass}`}><Icon className="h-5 w-5" /></div>
        <CardTitle className="text-lg font-semibold" style={{color: 'var(--text-primary)'}}>{title}</CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      <div className="comparison-grid">
        <div className="comparison-column">
          <div className="comparison-header">
            <Badge variant="outline" className="text-xs">{location1}</Badge>
            {freq1 && <Badge variant="outline" className="frequency-badge-sm">{freq1}</Badge>}
          </div>
          {data1 && Object.entries(data1).filter(([key]) => key !== 'description' && key !== 'frequency').map(([key, value]) => (
            <div key={key} className="comparison-item"><span className="comparison-label">{key.replace(/_/g, ' ')}</span><span className="comparison-value">{value}</span></div>
          ))}
        </div>
        <div className="comparison-column">
          <div className="comparison-header">
            <Badge variant="outline" className="text-xs">{location2}</Badge>
            {freq2 && <Badge variant="outline" className="frequency-badge-sm">{freq2}</Badge>}
          </div>
          {data2 && Object.entries(data2).filter(([key]) => key !== 'description' && key !== 'frequency').map(([key, value]) => (
            <div key={key} className="comparison-item"><span className="comparison-label">{key.replace(/_/g, ' ')}</span><span className="comparison-value">{value}</span></div>
          ))}
        </div>
      </div>
    </CardContent>
  </Card>
);

const CurrencyNotice = ({ currencyInfo }) => {
  if (!currencyInfo) return null;
  return (
    <div className="currency-notice animate-fadeIn" data-testid="currency-notice">
      <Globe className="h-4 w-4" />
      <div>
        <p className="currency-notice-text">Estimates in <strong>{currencyInfo.currency}</strong></p>
        <p className="currency-rate">1 USD = {currencyInfo.rate} {currencyInfo.code}</p>
      </div>
    </div>
  );
};

const ShareMenu = ({ estimates, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  if (!isOpen || !estimates) return null;

  const genText = () => {
    const house = estimates.house_price?.average || 'N/A';
    const rent = estimates.rent?.one_bed || 'N/A';
    const utils = estimates.utilities?.total_average || 'N/A';
    const ins = estimates.car_insurance?.average_annual || 'N/A';
    return `Cost of Living in ${estimates.location}\n\nHouse: ${house}\nRent: ${rent}\nUtilities: ${utils}\nInsurance: ${ins}\n\nGet your area's affordability → affordlyai.com`;
  };
  const shareText = genText();
  const shareUrl = "https://affordlyai.com";

  const handleCopy = async () => {
    haptic('success');
    try { await navigator.clipboard.writeText(shareText); setCopied(true); toast.success("Copied!"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("Failed to copy"); }
  };

  return (
    <>
      <div className="share-overlay" onClick={onClose}></div>
      <div className="share-menu animate-slideUp" data-testid="share-menu">
        <div className="share-menu-header">
          <h3>Share Results</h3>
          <button className="share-close-btn" onClick={() => { haptic('light'); onClose(); }} data-testid="share-close-btn"><X className="h-4 w-4" /></button>
        </div>
        <div className="share-preview">
          <p className="share-preview-location">{estimates.location}</p>
          <div className="share-preview-stats">
            <span>House: {estimates.house_price?.average}</span>
            <span>Rent: {estimates.rent?.one_bed}</span>
          </div>
        </div>
        <div className="share-options-grid">
          <button className="share-option" data-testid="share-whatsapp" onClick={() => { haptic('light'); window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank'); }}><span className="share-icon">💬</span><span>WhatsApp</span></button>
          <button className="share-option" data-testid="share-x" onClick={() => { haptic('light'); window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`${estimates.location} costs: ${estimates.house_price?.average} avg house, ${estimates.rent?.one_bed} rent. Get your area's affordability →`)}&url=${encodeURIComponent(shareUrl)}`, '_blank'); }}><span className="share-icon">𝕏</span><span>X</span></button>
          <button className="share-option" data-testid="share-tiktok" onClick={async () => { haptic('light'); await navigator.clipboard.writeText(`${estimates.location} cost of living\n\nHouse: ${estimates.house_price?.average}\nRent: ${estimates.rent?.one_bed}\nUtilities: ${estimates.utilities?.total_average}\n\n#costsofliving #housing #affordlyai`); toast.success("TikTok caption copied!"); }}><span className="share-icon">🎵</span><span>TikTok</span></button>
          <button className="share-option" data-testid="share-instagram" onClick={async () => { haptic('light'); await navigator.clipboard.writeText(`${estimates.location}\n\nHouse Price: ${estimates.house_price?.average}\n1-Bed Rent: ${estimates.rent?.one_bed}\nUtilities: ${estimates.utilities?.total_average}\nCar Insurance: ${estimates.car_insurance?.average_annual}\n\nKnow before you move.\n\n#costsofliving #realestate #affordlyai`); toast.success("Instagram caption copied!"); }}><span className="share-icon">📷</span><span>Instagram</span></button>
        </div>
        <button className="copy-link-btn" onClick={handleCopy} data-testid="share-copy-btn">
          {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          <span>{copied ? 'Copied!' : 'Copy to Clipboard'}</span>
        </button>
      </div>
    </>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="faq-item" data-testid="faq-item">
      <button className="faq-question" onClick={() => { haptic('light'); setIsOpen(!isOpen); }} data-testid="faq-question">
        <span>{question}</span>
        <ChevronDown className={`faq-icon ${isOpen ? 'open' : ''}`} />
      </button>
      {isOpen && <div className="faq-answer animate-slideDown" data-testid="faq-answer">{answer}</div>}
    </div>
  );
};

// City Carousel Component
const CityCarousel = ({ cities }) => {
  const trackRef = { current: null };
  const scroll = (dir) => {
    if (trackRef.current) {
      const amount = 300;
      trackRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
    }
  };
  return (
    <div className="carousel-wrapper" data-testid="city-carousel">
      <div className="carousel-track" ref={(el) => { trackRef.current = el; }}>
        {cities.map((slug) => {
          const city = LOCATIONS[slug];
          if (!city) return null;
          return (
            <Link to={`/location/${slug}`} className="carousel-card" key={slug} data-testid={`city-card-${slug}`}>
              {city.img && <img src={city.img} alt={city.name} className="carousel-card-img" loading="lazy" />}
              <div className="carousel-card-body">
                <h3><span>{city.flag}</span> {city.name}</h3>
                <p>{city.country}</p>
              </div>
            </Link>
          );
        })}
      </div>
      <div className="carousel-nav">
        <button className="carousel-nav-btn" onClick={() => scroll('left')} aria-label="Scroll left" data-testid="carousel-prev"><ChevronLeft className="h-5 w-5" /></button>
        <button className="carousel-nav-btn" onClick={() => scroll('right')} aria-label="Scroll right" data-testid="carousel-next"><ChevronRight className="h-5 w-5" /></button>
      </div>
    </div>
  );
};

// ==================== LANDING PAGE ====================
const LandingPage = () => {
  const navigate = useNavigate();
  const [userCity, setUserCity] = useState(null);
  const [userCurrency, setUserCurrency] = useState("£");

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const resp = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const d = await resp.json();
          const city = d.address?.city || d.address?.town || d.address?.state || d.address?.county;
          const cc = d.address?.country_code?.toUpperCase();
          if (city) setUserCity(city);
          if (cc && CURRENCY_MAP[cc]) setUserCurrency(CURRENCY_MAP[cc]);
        } catch {}
      }, () => {}, { timeout: 5000 });
    }
  }, []);

  const faqs = [
    { q: "How accurate are the cost estimates?", a: "Our estimates are AI-generated based on general market knowledge. They give you a useful starting point, but we always recommend verifying with local sources before making big decisions." },
    { q: "Which locations does AffordlyAI cover?", a: "Any city, town, postcode, or region worldwide. The AI adapts to local currencies and payment customs automatically." },
    { q: "Do I need to create an account?", a: "No. AffordlyAI is completely free to use with no sign-up. Your recent searches and saved areas are stored locally on your device." },
    { q: "How is rent frequency determined?", a: "We detect whether rent is typically paid monthly or yearly based on local customs. For example, rent in Nigeria is usually paid yearly, while UK rent is monthly." },
    { q: "Will you add real-time data?", a: "Yes. We're working on integrating official data sources including ONS, Land Registry, Zoopla, and Rightmove for real-time UK data." },
    { q: "Is my data stored anywhere?", a: "No. We don't store your location data on our servers. Your search history is saved locally on your device only." },
    { q: "Can I compare international locations?", a: "Absolutely. You can compare any two locations worldwide. Each location's data is shown in its local currency." }
  ];

  const featuredCities = ['london', 'manchester', 'lagos', 'abuja', 'birmingham', 'leeds', 'ibadan', 'edinburgh'];

  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Hero */}
      <section className="hero-section">
        <div className="hero-container animate-fadeIn">
          <div className="hero-eyebrow"><Eye className="h-3.5 w-3.5" /><span>Cost-of-Living Reality Check</span></div>
          <h1 className="hero-title animate-fadeInUp">Find Out If You're Overpaying to Live Where You Are</h1>
          <p className="hero-subtitle animate-fadeInUp delay-1">Get a quick, clear snapshot of housing, rent, utilities, and affordability for any area. Compare locations in minutes, not hours.</p>
          <div className="hero-ctas animate-fadeInUp delay-2">
            <button className="btn-primary" onClick={() => { haptic('medium'); navigate('/search'); }} data-testid="hero-cta-search">Search Your Area <ArrowRight className="h-4 w-4" /></button>
            <button className="btn-secondary" onClick={() => { haptic('light'); navigate('/compare'); }} data-testid="hero-cta-compare"><ArrowLeftRight className="h-4 w-4" /> Compare Two Areas</button>
          </div>
          <p className="hero-note animate-fadeIn delay-3">No sign-up required. Free to use.</p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="section value-section" data-testid="value-section">
        <div className="section-container">
          <p className="section-eyebrow">Why AffordlyAI</p>
          <h2 className="section-title">Stop Guessing. Start Knowing.</h2>
          <p className="section-subtitle">Most people have no idea what they'll actually pay until they've already signed the lease. We fix that.</p>
          <div className="value-grid">
            <div className="value-card animate-fadeInUp" data-testid="value-card-1">
              <div className="value-card-icon blue"><Search className="h-5 w-5" /></div>
              <h3>Stop Guessing</h3>
              <p>Get clear, data-backed estimates for housing, rent, and utilities in any area. No more piecing together information from ten different websites.</p>
            </div>
            <div className="value-card animate-fadeInUp delay-1" data-testid="value-card-2">
              <div className="value-card-icon orange"><TrendingDown className="h-5 w-5" /></div>
              <h3>Avoid Overspending</h3>
              <p>See exactly how your area compares to nearby alternatives before committing. A 15-minute check could save you thousands over a year.</p>
            </div>
            <div className="value-card animate-fadeInUp delay-2" data-testid="value-card-3">
              <div className="value-card-icon green"><Target className="h-5 w-5" /></div>
              <h3>Make Smarter Moves</h3>
              <p>Understand the full cost picture — not just rent, but utilities, insurance, and more. Move somewhere you can actually afford.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Impact */}
      <section className="section savings-section" data-testid="savings-section">
        <div className="section-container">
          <div className="savings-content">
            <p className="section-eyebrow">Savings Impact</p>
            <h2 className="savings-headline">
              {userCity ? `People in ${userCity} — are you overpaying?` : "How Much Could You Save?"}
            </h2>
            <p className="savings-desc">
              {userCity
                ? `Moving just 15 miles from ${userCity} could save you thousands in rent and utilities every year. Run the numbers before your next lease.`
                : "Moving just 15 miles could save you thousands in rent and utilities every year. The difference between areas is often bigger than people expect."
              }
            </p>
            <div className="savings-examples">
              <div className="savings-card animate-fadeInUp" data-testid="savings-card-1">
                <p className="savings-route">London → Manchester</p>
                <p className="savings-amount">~40%</p>
                <p className="savings-label">lower average rent</p>
              </div>
              <div className="savings-card animate-fadeInUp delay-1" data-testid="savings-card-2">
                <p className="savings-route">Lagos → Ibadan</p>
                <p className="savings-amount">~60%</p>
                <p className="savings-label">lower average rent</p>
              </div>
              <div className="savings-card animate-fadeInUp delay-2" data-testid="savings-card-3">
                <p className="savings-route">Edinburgh → Glasgow</p>
                <p className="savings-amount">~25%</p>
                <p className="savings-label">lower average rent</p>
              </div>
            </div>
            <button className="btn-primary" onClick={() => { haptic('medium'); navigate('/compare'); }} data-testid="savings-cta">
              Compare Your Area <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section how-section" data-testid="how-section">
        <div className="section-container">
          <p className="section-eyebrow">How It Works</p>
          <h2 className="section-title">Three Steps. Full Picture.</h2>
          <p className="section-subtitle">No accounts, no data collection, no hassle.</p>
          <div className="steps-grid">
            <div className="step-card animate-fadeInUp" data-testid="step-1">
              <div className="step-number">1</div>
              <div className="step-icon"><MapPin className="h-7 w-7" /></div>
              <h3>Enter a Location</h3>
              <p>Type any postcode, city name, or area. Works for UK, Nigeria, and anywhere worldwide.</p>
            </div>
            <div className="step-card animate-fadeInUp delay-1" data-testid="step-2">
              <div className="step-number">2</div>
              <div className="step-icon"><Zap className="h-7 w-7" /></div>
              <h3>Get Your Snapshot</h3>
              <p>See house prices, rent, utilities, car insurance, and an affordability score in seconds.</p>
            </div>
            <div className="step-card animate-fadeInUp delay-2" data-testid="step-3">
              <div className="step-number">3</div>
              <div className="step-icon"><ArrowLeftRight className="h-7 w-7" /></div>
              <h3>Compare and Decide</h3>
              <p>Compare two areas side by side. Save areas for later. Share results with anyone.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="section audience-section" data-testid="audience-section">
        <div className="section-container">
          <p className="section-eyebrow">Who It's For</p>
          <h2 className="section-title">Who It's For</h2>
          <p className="section-subtitle">AffordlyAI helps anyone making location decisions</p>
          <div className="audience-grid">
            <div className="audience-card animate-fadeInUp" data-testid="audience-renters">
              <div className="audience-card-icon"><Building2 className="h-6 w-6" /></div>
              <h3>Renters</h3>
              <p>Comparing neighbourhoods and finding affordable areas</p>
            </div>
            <div className="audience-card animate-fadeInUp delay-1" data-testid="audience-buyers">
              <div className="audience-card-icon"><HomeIcon className="h-6 w-6" /></div>
              <h3>First-Time Buyers</h3>
              <p>Understanding true costs before purchasing</p>
            </div>
            <div className="audience-card animate-fadeInUp delay-2" data-testid="audience-investors">
              <div className="audience-card-icon"><PiggyBank className="h-6 w-6" /></div>
              <h3>Property Investors</h3>
              <p>Evaluating areas for rental yield potential</p>
            </div>
            <div className="audience-card animate-fadeInUp delay-3" data-testid="audience-students">
              <div className="audience-card-icon"><GraduationCap className="h-6 w-6" /></div>
              <h3>Students</h3>
              <p>Choosing where to study based on living costs</p>
            </div>
            <div className="audience-card animate-fadeInUp delay-4" data-testid="audience-professionals">
              <div className="audience-card-icon"><Briefcase className="h-6 w-6" /></div>
              <h3>Professionals</h3>
              <p>Relocating for work opportunities</p>
            </div>
            <div className="audience-card animate-fadeInUp delay-5" data-testid="audience-families">
              <div className="audience-card-icon"><Users className="h-6 w-6" /></div>
              <h3>Families</h3>
              <p>Planning moves with budget in mind</p>
            </div>
          </div>
        </div>
      </section>

      {/* City Showcase - Carousel */}
      <section className="section cities-section" data-testid="cities-section">
        <div className="section-container">
          <p className="section-eyebrow">Popular Locations</p>
          <h2 className="section-title">Explore Cost of Living by City</h2>
          <p className="section-subtitle">Pre-built profiles for major cities in the UK and Nigeria. Click any city to learn more.</p>
          <CityCarousel cities={featuredCities} />
        </div>
      </section>

      {/* FAQ */}
      <section className="section faq-section" data-testid="faq-section">
        <div className="section-container">
          <p className="section-eyebrow">FAQ</p>
          <h2 className="section-title">Common Questions</h2>
          <p className="section-subtitle">Quick answers to things people ask us.</p>
          <div className="faq-container">
            {faqs.map((faq, i) => <FAQItem key={i} question={faq.q} answer={faq.a} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section" data-testid="cta-section">
        <div className="cta-card animate-fadeInUp">
          <h2>Ready to check your area?</h2>
          <p>Get your cost-of-living snapshot in seconds. Free, no sign-up.</p>
          <button className="btn-primary" onClick={() => { haptic('medium'); navigate('/search'); }} data-testid="cta-search-btn">Search Your Area <ArrowRight className="h-4 w-4" /></button>
          <p className="hero-note" style={{marginTop: '0.75rem'}}>Thousands of areas. One clear picture.</p>
        </div>
      </section>
    </div>
  );
};

// ==================== SEARCH PAGE ====================
const SearchPage = () => {
  const [location, setLocation] = useState("");
  const [loading, setLoading] = useState(false);
  const [estimates, setEstimates] = useState(null);
  const [currencyInfo, setCurrencyInfo] = useState(null);
  const [detectingLocation, setDetectingLocation] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const navigate = useNavigate();
  const routeLocation = useLocation();

  useEffect(() => {
    if (routeLocation.state?.preloadedData) {
      const { data } = routeLocation.state.preloadedData;
      setEstimates(data);
      setLocation(data.location || routeLocation.state.preloadedData.location);
      if (data.currency_info) setCurrencyInfo(data.currency_info);
      setIsSaved(isAreaSaved(data.location || routeLocation.state.preloadedData.location));
      window.history.replaceState({}, document.title);
    }
  }, [routeLocation.state]);

  const detectUserLocation = () => {
    haptic('light');
    if (!navigator.geolocation) { toast.error("Geolocation not supported"); return; }
    setDetectingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
          const data = await response.json();
          const loc = data.address?.city || data.address?.town || data.address?.village || data.address?.county || "Unknown";
          setLocation(loc);
          haptic('success'); toast.success(`Detected: ${loc}`);
        } catch { haptic('error'); toast.error("Failed to detect location"); }
        finally { setDetectingLocation(false); }
      },
      () => { haptic('error'); toast.error("Unable to get location"); setDetectingLocation(false); }
    );
  };

  const searchCosts = async () => {
    if (!location.trim()) { haptic('error'); toast.error("Please enter a location"); return; }
    haptic('medium'); setLoading(true); setEstimates(null); setCurrencyInfo(null);
    try {
      const response = await axios.post(`${API}/estimate`, { location });
      setEstimates(response.data);
      if (response.data.currency_info) setCurrencyInfo(response.data.currency_info);
      saveRecentSearch(location, response.data);
      setIsSaved(isAreaSaved(response.data.location || location));
      haptic('success'); toast.success("Estimates ready!");
    } catch { haptic('error'); toast.error("Failed to get estimates. Try again."); }
    finally { setLoading(false); }
  };

  const handleSave = () => {
    if (!estimates) return;
    if (isSaved) {
      removeArea(estimates.location || location);
      setIsSaved(false);
      toast.success("Removed from saved areas");
    } else {
      saveArea(estimates.location || location, estimates);
      setIsSaved(true);
      toast.success("Saved for later!");
    }
    haptic('success');
  };

  const goBack = () => { haptic('light'); setEstimates(null); setCurrencyInfo(null); setLocation(""); };

  return (
    <div className="page-container" data-testid="search-page">
      <LoadingOverlay isVisible={loading} />
      {!estimates ? (
        <div className="search-hero animate-fadeIn">
          <h1 className="page-title">Search Location</h1>
          <p className="page-subtitle">Enter a postcode or location to see what it actually costs to live there</p>
          <div className="search-box" data-testid="search-box">
            <div className="search-input-group">
              <div className="input-wrapper">
                <MapPin className="input-icon" />
                <Input type="text" placeholder="Enter location or postcode" value={location} onChange={(e) => setLocation(e.target.value)} className="search-input" onKeyDown={(e) => e.key === 'Enter' && searchCosts()} data-testid="search-input" />
              </div>
              <Button variant="outline" onClick={detectUserLocation} disabled={detectingLocation} className="detect-btn" data-testid="detect-btn">
                {detectingLocation ? <Loader2 className="h-5 w-5 animate-spin" /> : <Navigation className="h-5 w-5" />}
              </Button>
              <button onClick={searchCosts} disabled={loading || !location.trim()} className="search-btn" data-testid="search-btn">Get Estimates</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="results-section animate-fadeIn" data-testid="search-results">
          <div className="results-header">
            <div className="results-actions">
              <button className="back-btn" onClick={goBack} data-testid="back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
              <button className="action-btn" onClick={() => { haptic('light'); setEstimates(null); setCurrencyInfo(null); }} data-testid="new-search-btn"><Search className="h-4 w-4" />New Search</button>
              <button className={`save-btn ${isSaved ? 'saved' : ''}`} onClick={handleSave} data-testid="save-btn">
                {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
                {isSaved ? 'Saved' : 'Save Area'}
              </button>
              <button className="share-btn" onClick={() => { haptic('light'); setShareMenuOpen(true); }} data-testid="share-btn"><Share2 className="h-4 w-4" />Share</button>
            </div>
            <h2 className="results-title"><MapPin className="h-5 w-5" style={{color: 'var(--brand)'}}/>{estimates.location}</h2>
          </div>
          <ShareMenu estimates={estimates} isOpen={shareMenuOpen} onClose={() => setShareMenuOpen(false)} />
          {estimates.affordability_score && <AffordabilityScore score={estimates.affordability_score} />}
          <CurrencyNotice currencyInfo={currencyInfo} />
          <div className="cost-cards-grid">
            <CostCard icon={HomeIcon} title="House Prices" data={estimates.house_price} colorClass="icon-house" />
            <CostCard icon={Building2} title="Rent" data={estimates.rent} colorClass="icon-rent" frequencyLabel={estimates.rent?.frequency === 'yearly' ? 'Per Year' : 'Per Month'} />
            <CostCard icon={Lightbulb} title="Utilities" data={estimates.utilities} colorClass="icon-utilities" frequencyLabel={estimates.utilities?.frequency === 'yearly' ? 'Per Year' : 'Per Month'} />
            <CostCard icon={Car} title="Car Insurance" data={estimates.car_insurance} colorClass="icon-insurance" frequencyLabel="Per Year" />
          </div>
          <Card className="summary-card animate-fadeInUp"><CardHeader><CardTitle className="text-lg" style={{color: 'var(--text-primary)'}}>Summary</CardTitle></CardHeader><CardContent><p style={{color: 'var(--text-secondary)', lineHeight: 1.65}}>{estimates.summary}</p></CardContent></Card>
          <div className="disclaimer animate-fadeIn"><p><strong>Note:</strong> These figures are AI-generated estimates based on general market knowledge. Not real-time data. Not financial advice.</p></div>
        </div>
      )}
    </div>
  );
};

// ==================== COMPARE PAGE ====================
const ComparePage = () => {
  const [location1, setLocation1] = useState("");
  const [location2, setLocation2] = useState("");
  const [loading, setLoading] = useState(false);
  const [comparison, setComparison] = useState(null);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const compareLocations = async () => {
    if (!location1.trim() || !location2.trim()) { haptic('error'); toast.error("Please enter both locations"); return; }
    haptic('medium'); setLoading(true); setComparison(null);
    try {
      const response = await axios.post(`${API}/compare`, { location1, location2 });
      setComparison(response.data);
      haptic('success'); toast.success("Comparison ready!");
    } catch { haptic('error'); toast.error("Failed to compare. Try again."); }
    finally { setLoading(false); }
  };

  const goBack = () => { haptic('light'); setComparison(null); };
  const shareEstimates = comparison ? { location: `${comparison.location1} vs ${comparison.location2}`, house_price: comparison.location1_data?.house_price, rent: comparison.location1_data?.rent, utilities: comparison.location1_data?.utilities, car_insurance: comparison.location1_data?.car_insurance } : null;

  return (
    <div className="page-container" data-testid="compare-page">
      <LoadingOverlay isVisible={loading} />
      {!comparison ? (
        <>
          <div className="page-header animate-fadeIn">
            <h1 className="page-title">Compare Areas</h1>
            <p className="page-subtitle">See how costs differ between two locations side-by-side</p>
          </div>
          <Card className="compare-card animate-fadeInUp">
            <CardContent className="pt-6">
              <div className="compare-form">
                <div className="compare-input-wrapper">
                  <label className="input-label">First Location</label>
                  <div className="input-wrapper"><MapPin className="input-icon" /><Input type="text" placeholder="e.g., Manchester" value={location1} onChange={(e) => setLocation1(e.target.value)} className="compare-input" data-testid="compare-input-1" /></div>
                </div>
                <div className="compare-vs"><span>VS</span></div>
                <div className="compare-input-wrapper">
                  <label className="input-label">Second Location</label>
                  <div className="input-wrapper"><MapPin className="input-icon" /><Input type="text" placeholder="e.g., Birmingham" value={location2} onChange={(e) => setLocation2(e.target.value)} className="compare-input" data-testid="compare-input-2" /></div>
                </div>
              </div>
              <button onClick={compareLocations} disabled={loading || !location1.trim() || !location2.trim()} className="compare-btn" data-testid="compare-btn">Compare Locations</button>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="results-section animate-fadeIn" data-testid="compare-results">
          <div className="results-header">
            <div className="results-actions">
              <button className="back-btn" onClick={goBack} data-testid="compare-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
              <button className="action-btn" onClick={() => setComparison(null)} data-testid="new-compare-btn"><ArrowLeftRight className="h-4 w-4" />New Compare</button>
              <button className="share-btn" onClick={() => { haptic('light'); setShareMenuOpen(true); }} data-testid="compare-share-btn"><Share2 className="h-4 w-4" />Share</button>
            </div>
            <h2 className="results-title"><ArrowLeftRight className="h-5 w-5" style={{color: 'var(--brand)'}}/>{comparison.location1} vs {comparison.location2}</h2>
          </div>
          <ShareMenu estimates={shareEstimates} isOpen={shareMenuOpen} onClose={() => setShareMenuOpen(false)} />
          <div className="comparison-cards-grid">
            <ComparisonCard icon={HomeIcon} title="House Prices" data1={comparison.location1_data?.house_price} data2={comparison.location2_data?.house_price} location1={comparison.location1} location2={comparison.location2} colorClass="icon-house" />
            <ComparisonCard icon={Building2} title="Rent" data1={comparison.location1_data?.rent} data2={comparison.location2_data?.rent} location1={comparison.location1} location2={comparison.location2} colorClass="icon-rent" freq1={comparison.location1_data?.rent?.frequency === 'yearly' ? 'Yearly' : 'Monthly'} freq2={comparison.location2_data?.rent?.frequency === 'yearly' ? 'Yearly' : 'Monthly'} />
            <ComparisonCard icon={Lightbulb} title="Utilities" data1={comparison.location1_data?.utilities} data2={comparison.location2_data?.utilities} location1={comparison.location1} location2={comparison.location2} colorClass="icon-utilities" freq1={comparison.location1_data?.utilities?.frequency === 'yearly' ? 'Yearly' : 'Monthly'} freq2={comparison.location2_data?.utilities?.frequency === 'yearly' ? 'Yearly' : 'Monthly'} />
            <ComparisonCard icon={Car} title="Car Insurance" data1={comparison.location1_data?.car_insurance} data2={comparison.location2_data?.car_insurance} location1={comparison.location1} location2={comparison.location2} colorClass="icon-insurance" freq1="Yearly" freq2="Yearly" />
          </div>
          <Card className="insights-card animate-fadeInUp"><CardHeader><CardTitle className="text-lg" style={{color: 'var(--text-primary)'}}>AI Insights</CardTitle></CardHeader><CardContent><p style={{color: 'var(--text-secondary)', lineHeight: 1.65}}>{comparison.comparison_insights}</p></CardContent></Card>
          <div className="disclaimer animate-fadeIn"><p><strong>Note:</strong> AI-generated estimates. Not real-time data. Not financial advice.</p></div>
        </div>
      )}
    </div>
  );
};

// ==================== RECENT PAGE ====================
const RecentPage = () => {
  const [recentSearches, setRecentSearches] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { setRecentSearches(getRecentSearches()); }, []);

  return (
    <div className="page-container" data-testid="recent-page">
      <div className="page-header animate-fadeIn"><h1 className="page-title">Recent Searches</h1><p className="page-subtitle">Your search history, stored locally on this device</p></div>
      <Card className="recent-card animate-fadeInUp">
        <CardContent className="pt-6">
          {recentSearches.length === 0 ? (
            <div className="empty-state" data-testid="recent-empty">
              <Clock className="h-16 w-16 mx-auto mb-4" style={{color: 'var(--border)'}} />
              <p style={{color: 'var(--text-secondary)', fontSize: '1.05rem'}}>No recent searches yet</p>
              <p style={{color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem'}}>Your search history will appear here</p>
              <Button className="mt-4" onClick={() => navigate('/search')} data-testid="recent-start-btn">Start Searching</Button>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="recent-list">
                {recentSearches.map((search, index) => (
                  <div key={index} className="recent-item" onClick={() => { haptic('light'); navigate('/search', { state: { preloadedData: search } }); }} data-testid={`recent-item-${index}`}>
                    <div className="recent-item-left">
                      <div className="recent-icon"><MapPin className="h-4 w-4" /></div>
                      <div>
                        <p className="recent-location">{search.location}</p>
                        <p className="recent-time">{new Date(search.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5" style={{color: 'var(--text-muted)'}} />
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== SAVED PAGE ====================
const SavedPage = () => {
  const [savedAreas, setSavedAreas] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { setSavedAreas(getSavedAreas()); }, []);

  const handleRemove = (e, location) => {
    e.stopPropagation();
    haptic('light');
    removeArea(location);
    setSavedAreas(getSavedAreas());
    toast.success("Removed from saved areas");
  };

  return (
    <div className="page-container" data-testid="saved-page">
      <div className="page-header animate-fadeIn"><h1 className="page-title">Saved Areas</h1><p className="page-subtitle">Areas you've bookmarked for later comparison</p></div>
      <Card className="saved-card animate-fadeInUp">
        <CardContent className="pt-6">
          {savedAreas.length === 0 ? (
            <div className="empty-state" data-testid="saved-empty">
              <Bookmark className="h-16 w-16 mx-auto mb-4" style={{color: 'var(--border)'}} />
              <p style={{color: 'var(--text-secondary)', fontSize: '1.05rem'}}>No saved areas yet</p>
              <p style={{color: 'var(--text-muted)', fontSize: '0.85rem', marginTop: '0.25rem'}}>Search for an area and click "Save Area" to bookmark it</p>
              <Button className="mt-4" onClick={() => navigate('/search')} data-testid="saved-start-btn">Search Areas</Button>
            </div>
          ) : (
            <ScrollArea className="h-[500px]">
              <div className="saved-list">
                {savedAreas.map((area, index) => (
                  <div key={index} className="saved-item" onClick={() => { haptic('light'); navigate('/search', { state: { preloadedData: area } }); }} data-testid={`saved-item-${index}`}>
                    <div className="saved-item-left">
                      <div className="saved-icon"><Bookmark className="h-4 w-4" /></div>
                      <div>
                        <p className="saved-location">{area.location}</p>
                        <p className="saved-time">Saved {new Date(area.timestamp).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                      </div>
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                      {area.data?.affordability_score && <span className={`saved-score ${getScoreClass(area.data.affordability_score)}`} style={{color: getScoreClass(area.data.affordability_score) === 'excellent' ? 'var(--success)' : getScoreClass(area.data.affordability_score) === 'good' ? 'var(--brand)' : getScoreClass(area.data.affordability_score) === 'average' ? 'var(--warning)' : 'var(--error)'}}>{area.data.affordability_score}</span>}
                      <button className="remove-btn" onClick={(e) => handleRemove(e, area.location)} data-testid={`remove-saved-${index}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== LOCATION PAGE ====================
const LocationPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const city = LOCATIONS[slug];

  useEffect(() => {
    if (city) document.title = `Cost of Living in ${city.name} 2026 | AffordlyAI`;
    return () => { document.title = "AffordlyAI | Cost of Living Estimator"; };
  }, [city, slug]);

  if (!city) {
    return (
      <div className="page-container" data-testid="location-not-found">
        <div className="empty-state" style={{paddingTop: '4rem'}}>
          <MapPin className="h-16 w-16 mx-auto mb-4" style={{color: 'var(--border)'}} />
          <p style={{color: 'var(--text-secondary)', fontSize: '1.1rem', marginBottom: '0.5rem'}}>Location not found</p>
          <p style={{color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: '1.5rem'}}>We don't have a profile for this location yet.</p>
          <button className="btn-primary" onClick={() => navigate('/search')}>Search Instead</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="location-page">
      <div className="location-hero animate-fadeIn">
        <div className="location-flag">{city.flag}</div>
        <h1 className="location-title">Cost of Living in {city.name}</h1>
        <p className="location-country">{city.country}</p>
        <div className="location-facts">
          <div className="location-fact" data-testid="location-pop"><div className="location-fact-value">{city.pop}</div><div className="location-fact-label">Population</div></div>
          <div className="location-fact" data-testid="location-rent"><div className="location-fact-value">{city.avgRent}</div><div className="location-fact-label">Avg Rent</div></div>
          <div className="location-fact" data-testid="location-house"><div className="location-fact-value">{city.avgHouse}</div><div className="location-fact-label">Avg House Price</div></div>
        </div>
        <p className="location-description">{city.desc}</p>
        <div className="location-cta">
          <button className="btn-primary" onClick={() => { haptic('medium'); navigate('/search'); }} data-testid="location-search-btn">
            Search {city.name} <ArrowRight className="h-4 w-4" />
          </button>
          <button className="btn-secondary" onClick={() => { haptic('light'); navigate('/compare'); }} data-testid="location-compare-btn">
            <ArrowLeftRight className="h-4 w-4" /> Compare with Another Area
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== ABOUT PAGE ====================
const AboutPage = () => (
  <div className="page-container" data-testid="about-page">
    <div className="content-page animate-fadeIn">
      <h1 className="content-title">About AffordlyAI</h1>
      <div className="content-section">
        <h2>What We Do</h2>
        <p>AffordlyAI gives you a clear, honest picture of what it costs to live somewhere. Enter any location — a postcode, a city, a neighbourhood — and we'll show you house prices, rent, utility bills, car insurance, and an overall affordability score.</p>
        <p>You can compare two areas side by side, save areas for later, and share results with anyone. No account needed. No data collected.</p>
      </div>
      <div className="content-section">
        <h2>Why We Built This</h2>
        <p>Understanding the true cost of living in a new area takes hours of research across dozens of websites. Most people don't do it properly — and end up surprised by what they're actually paying.</p>
        <p>We wanted to put all of that information in one place, make it simple, and make it free. Whether you're a first-time buyer working out if you can afford a mortgage, a renter comparing neighbourhoods, or a family planning a move — you deserve clear numbers before you commit.</p>
      </div>
      <div className="content-section">
        <h2>How the Data Works</h2>
        <p>Right now, AffordlyAI uses AI to generate estimates based on general market knowledge. This gives you a useful starting point, but it's not real-time data and shouldn't replace proper financial advice.</p>
        <p>We're actively integrating official data sources — ONS, Land Registry, Zoopla, Rightmove, and more — to make estimates increasingly accurate over time.</p>
      </div>
    </div>
  </div>
);

// ==================== PRIVACY PAGE ====================
const PrivacyPage = () => (
  <div className="page-container" data-testid="privacy-page">
    <div className="content-page animate-fadeIn">
      <h1 className="content-title">Privacy Policy</h1>
      <p className="content-date">Last updated: {new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</p>
      <div className="content-section">
        <h2>The Short Version</h2>
        <p>We don't collect your personal data. We don't track you. We don't sell anything to anyone. Your search history stays on your device.</p>
      </div>
      <div className="content-section">
        <h2>What Happens When You Search</h2>
        <p>When you search for a location, we process your query to generate cost estimates. We don't permanently store your search data on our servers. Your search history and saved areas are stored locally in your browser, which you can clear at any time.</p>
      </div>
      <div className="content-section">
        <h2>Analytics</h2>
        <p>We use Plausible Analytics — a privacy-friendly service that doesn't use cookies and doesn't collect personal data. It tells us how many people visit the site, not who they are.</p>
      </div>
      <div className="content-section">
        <h2>What We Don't Do</h2>
        <ul>
          <li>We don't require sign-up or accounts</li>
          <li>We don't collect names, emails, or personal information</li>
          <li>We don't use tracking cookies</li>
          <li>We don't sell or share data with third parties</li>
          <li>We don't store your precise geolocation</li>
        </ul>
      </div>
    </div>
  </div>
);

// ==================== MAIN APP ====================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<><Header /><LandingPage /><Footer /></>} />
          <Route path="/search" element={<><Header /><SearchPage /><Footer /></>} />
          <Route path="/compare" element={<><Header /><ComparePage /><Footer /></>} />
          <Route path="/recent" element={<><Header /><RecentPage /><Footer /></>} />
          <Route path="/saved" element={<><Header /><SavedPage /><Footer /></>} />
          <Route path="/location/:slug" element={<><Header /><LocationPage /><Footer /></>} />
          <Route path="/about" element={<><Header /><AboutPage /><Footer /></>} />
          <Route path="/privacy" element={<><Header /><PrivacyPage /><Footer /></>} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
